// Camera.jsx

import React, { useCallback, useEffect, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Utilities
import { generateKey } from 'utilities/Keys';
import { getDefaultNode } from 'utilities/NodeFactory';
import { buildUserSummary } from 'utilities/Users';

// CSS
import './Camera.css';

// Components
import RecordingTimer from './recordingtimer/RecordingTimer';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * Camera
 * 
 * Renders a camera interface for taking photos or recording videos.
 * 
 * @returns {ReactNode} - Camera component.
 */
const Camera = ({ contentType, onContentCreated }) => {

    // ----- CONTEXT ----- //

    // User Context
    const { user } = useUser();
    const { theme } = useTheme();

    // Workspace Context
    const { activeWorkspace } = useWorkspace();

    // ----- STATE ----- //

    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [cameras, setCameras] = useState([]);
    const [selectedCameraId, setSelectedCameraId] = useState(null);


    // ----- REFERENCES ----- //

    const chunksRef = useRef([]);
    const mediaStreamRef = useRef(null);
    const videoRef = useRef(null);

    // ----- AUDIO ----- //

    const shutterSound = new Audio('/sounds/camera.mp3');

    // ----- UTILITIES ----- //

    /**
     * Enumerates available video input devices (cameras) and updates state.
     * 
     * @returns {Promise<void>} - Resolves when enumeration is complete.
     */
    const enumerateCameras = useCallback(async () => {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
            return;
        }

        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            setCameras(videoDevices);

            if (videoDevices.length === 0) {
                setSelectedCameraId(prev => (prev === null ? prev : null));
                return;
            }

            const activeTrackDeviceId = mediaStreamRef.current?.getVideoTracks()[0]?.getSettings()?.deviceId;

            setSelectedCameraId(prev => {
                if (prev && videoDevices.some(device => device.deviceId === prev)) {
                    return prev;
                }

                if (activeTrackDeviceId && videoDevices.some(device => device.deviceId === activeTrackDeviceId)) {
                    return activeTrackDeviceId;
                }

                return videoDevices[0].deviceId;
            });
        } catch (err) {
            console.error('Camera enumeration failed:', err);
        }
    }, []);

    /**
     * Starts the camera with the specified device ID or default front camera.
     * 
     * @param {string|null} deviceId - Optional device ID of the camera to use.
     * 
     * @returns {Promise<void>} - Resolves when the camera has started.
     */
    const startCamera = useCallback(async (deviceId = null) => {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
            console.error('Media devices API is not supported in this environment.');
            return;
        }

        if (isRecording) {
            console.warn('Cannot switch cameras while recording.');
            return;
        }

        mediaStreamRef.current?.getTracks().forEach(track => track.stop());

        try {
            const constraints = {
                video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'user' },
                audio: true,
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            mediaStreamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }

            const activeDeviceId = stream.getVideoTracks()[0]?.getSettings()?.deviceId || null;
            if (activeDeviceId) {
                setSelectedCameraId(prev => (prev === activeDeviceId ? prev : activeDeviceId));
            }

            await enumerateCameras();
        } catch (err) {
            console.error('Camera access failed:', err);
        }
    }, [enumerateCameras, isRecording]);

    /**
     * Stops the camera and releases media resources.
     */
    const stopCamera = () => {
        if (mediaRecorder && isRecording) {
            mediaRecorder.stop();
        }
        mediaStreamRef.current?.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
    };

    // ----- EFFECTS ----- //

    // Initialize camera on mount
    useEffect(() => {
        startCamera();
        return () => stopCamera();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (typeof navigator === 'undefined') {
            return;
        }

        const mediaDevices = navigator.mediaDevices;
        if (!mediaDevices) {
            return;
        }

        const handleDeviceChange = () => {
            enumerateCameras();
        };

        mediaDevices.addEventListener?.('devicechange', handleDeviceChange);

        return () => {
            mediaDevices.removeEventListener?.('devicechange', handleDeviceChange);
        };
    }, [enumerateCameras]);

    // ----- CONSTANTS ----- //

    const currentCameraId = selectedCameraId ?? (cameras[0]?.deviceId ?? '');

    // ----- HANDLERS ----- //

    const handleCameraChange = async (event) => {
        const newCameraId = event.target.value;
        if (!newCameraId || newCameraId === selectedCameraId) {
            return;
        }

        setSelectedCameraId(newCameraId);
        await startCamera(newCameraId);
    };

    /**
     * Captures a frame from the video stream, plays a shutter sound, and creates a photo content item.
     */
    const handleTakePhoto = () => {
        try {
            shutterSound.currentTime = 0;
            shutterSound.play();
        } catch (err) {
            console.warn('Shutter sound playback failed:', err);
        }

        const video = videoRef.current;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(async (blob) => {
            if (blob) {
                const mimeType = blob.type || 'image/jpeg';
                const timestamp = Date.now();
                const fallbackName = `snapshot-${timestamp}.jpg`;

                let localFile;
                if (typeof File === 'function') {
                    try {
                        localFile = new File([blob], fallbackName, { type: mimeType });
                    } catch (error) {
                        localFile = blob;
                    }
                } else {
                    localFile = blob;
                }

                if (!(localFile instanceof File)) {
                    try {
                        Object.defineProperty(localFile, 'name', {
                            value: fallbackName,
                            configurable: true,
                        });
                    } catch {
                        // ignore inability to define name
                    }
                    try {
                        Object.defineProperty(localFile, 'type', {
                            value: mimeType,
                            configurable: true,
                        });
                    } catch {
                        // ignore inability to define type
                    }
                }

                const fileName = (localFile && localFile.name) || fallbackName;
                const objectUrl = URL.createObjectURL(localFile);

                const defaultNode = await getDefaultNode('PHOTO', activeWorkspace?.key || null);
                const item = {
                    ...defaultNode,
                    key: generateKey(),
                    title: null,
                    userKey: user.key,
                    user: buildUserSummary(user),
                    workspaceKey: activeWorkspace?.key || null,
                    url: objectUrl,
                    localUri: objectUrl,
                    base64Data: null,
                    localFile,
                    tempFile: localFile,
                    fileName,
                    mimeType,
                };

                onContentCreated(item);
                stopCamera(); // optional: turn off after photo
            }
        }, 'image/jpeg');
    };

    /**
     * Starts video recording, collects data chunks, and creates a video content item upon stopping.
     */
    const handleStartRecording = () => {
        if (isRecording || !mediaStreamRef.current) return;

        const stream = mediaStreamRef.current;
        const recorder = new MediaRecorder(stream);
        chunksRef.current = [];

        recorder.ondataavailable = e => {
            if (e.data.size > 0) {
                chunksRef.current.push(e.data);
            }
        };

        recorder.onstop = async () => {
            const videoBlob = new Blob(chunksRef.current, { type: 'video/webm' });
            const mimeType = videoBlob.type || 'video/webm';
            const timestamp = Date.now();
            const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
            const fallbackName = `recording-${timestamp}.${extension}`;

            let localFile;
            if (typeof File === 'function') {
                try {
                    localFile = new File([videoBlob], fallbackName, { type: mimeType });
                } catch (error) {
                    localFile = videoBlob;
                }
            } else {
                localFile = videoBlob;
            }

            if (!(localFile instanceof File)) {
                try {
                    Object.defineProperty(localFile, 'name', {
                        value: fallbackName,
                        configurable: true,
                    });
                } catch {
                    // ignore inability to define name
                }
                try {
                    Object.defineProperty(localFile, 'type', {
                        value: mimeType,
                        configurable: true,
                    });
                } catch {
                    // ignore inability to define type
                }
            }

            const fileName = (localFile && localFile.name) || fallbackName;
            const uri = URL.createObjectURL(localFile);

            const defaultNode = await getDefaultNode('VIDEO', activeWorkspace?.key || null);
            const item = {
                ...defaultNode,
                key: generateKey(),
                title: null,
                userKey: user.key,
                user: buildUserSummary(user),
                workspaceKey: activeWorkspace?.key || null,
                url: uri,
                localUri: uri,
                localFile,
                tempFile: localFile,
                fileName,
                mimeType,
                base64Data: null,
            };

            onContentCreated(item);

            setIsRecording(false);
            setMediaRecorder(null);
        };

        recorder.start();
        setMediaRecorder(recorder);
        setIsRecording(true);
    };

    /**
     * Stops video recording.
     */
    const handleStopRecording = () => {
        stopCamera();
    };

    /**
     * Handles click on the start/stop button for photo capture or video recording.
     */
    const handleStartClick = () => {
        if (contentType === 'SNAPSHOT') {
            handleTakePhoto();
        } else if (contentType === 'RECORDING') {
            if (isRecording) {
                handleStopRecording();
            } else {
                handleStartRecording();
            }
        }
    };

    return (
        <div
            className="camera-container"
            style={{
                '--camera-overlay': theme.overlay,
            }}
        >
            {(contentType === 'RECORDING') && isRecording && (
                <div className="camera-header">
                    <RecordingTimer
                        isRecording={isRecording}
                        onMaxDurationReached={handleStopRecording}
                    />
                </div>
            )}

            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-preview"
            />

            <div className="camera-controls">

            {/* BUTTON */}
                <div
                    className="camera-start-button-wrapper"
                    style={{ borderColor: 'white' }}
                >
                    <div
                        className="camera-start-button"
                        style={{
                            backgroundColor:
                                contentType === 'SNAPSHOT'
                                    ? '#ffffff'
                                    : contentType === 'RECORDING' && isRecording
                                        ? 'transparent'
                                        : 'red',
                        }}
                        onClick={handleStartClick}
                    >
                        {contentType === 'RECORDING' && isRecording && (
                            <div className="camera-start-button-stop-square"></div>
                        )}
                    </div>
                </div>

            {/* CAMERA LIST */}
            {cameras.length > 0 && (
                <div className="camera-device-selector">
                    <SelectInput
                        placeholder="Select a node type"
                        value={currentCameraId}
                        onChange={handleCameraChange}
                        enabled={!isRecording}
                    >
                        {cameras.map((camera, index) => (
                            <option key={camera.deviceId} value={camera.deviceId}>
                                {camera.label || `Camera ${index + 1}`}
                            </option>
                        ))}
                    </SelectInput>
                </div>
            )}
            </div>
        </div>
    );
};

export default Camera;
